import { useState, useEffect, useCallback } from 'react';
import { supabase, withRetry } from '../lib/supabase';
import type { TimeEntry } from '../types';

export function useTimeEntries(employeeId?: string, payPeriodStart?: string, payPeriodEnd?: string) {
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTimeEntries = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('time_entries')
        .select('*')
        .order('clock_in_time', { ascending: false });

      if (employeeId) {
        query = query.eq('employee_id', employeeId);
      }

      if (payPeriodStart && payPeriodEnd) {
        query = query
          .gte('pay_period_start', payPeriodStart)
          .lte('pay_period_end', payPeriodEnd);
      }

      const result = await withRetry(async () => query);

      if (result.error) throw result.error;

      const entries: TimeEntry[] = (result.data || []).map((entry: any) => ({
        id: entry.id,
        employeeId: entry.employee_id,
        clockInTime: entry.clock_in_time,
        clockOutTime: entry.clock_out_time,
        clockInLatitude: entry.clock_in_latitude,
        clockInLongitude: entry.clock_in_longitude,
        clockOutLatitude: entry.clock_out_latitude,
        clockOutLongitude: entry.clock_out_longitude,
        hoursWorked: entry.hours_worked,
        status: entry.status,
        payPeriodStart: entry.pay_period_start,
        payPeriodEnd: entry.pay_period_end,
        notes: entry.notes,
        approvedBy: entry.approved_by,
        approvedAt: entry.approved_at,
        createdAt: entry.created_at,
        updatedAt: entry.updated_at
      }));

      setTimeEntries(entries);
    } catch (err) {
      console.error('Error loading time entries:', err);
      setError(err instanceof Error ? err.message : 'Failed to load time entries');
    } finally {
      setLoading(false);
    }
  }, [employeeId, payPeriodStart, payPeriodEnd]);

  useEffect(() => {
    fetchTimeEntries();
  }, [fetchTimeEntries]);

  const clockIn = async (
    employeeId: string,
    latitude: number | null,
    longitude: number | null,
    payPeriodStart: string,
    payPeriodEnd: string
  ) => {
    try {
      setError(null);

      console.log('clockIn called with:', {
        employeeId,
        latitude,
        longitude,
        payPeriodStart,
        payPeriodEnd
      });

      const insertData = {
        employee_id: employeeId,
        clock_in_time: new Date().toISOString(),
        clock_in_latitude: latitude,
        clock_in_longitude: longitude,
        pay_period_start: payPeriodStart,
        pay_period_end: payPeriodEnd,
        status: 'pending' as const
      };

      console.log('Inserting time entry:', insertData);

      const { data, error: insertError } = await withRetry(async () => {
        return await supabase
          .from('time_entries')
          .insert(insertData)
          .select();
      });

      if (insertError) {
        console.error('Insert error:', insertError);
        throw insertError;
      }

      console.log('Clock in successful:', data);
      await fetchTimeEntries();
    } catch (err) {
      console.error('Error clocking in:', err);
      throw err;
    }
  };

  const clockOut = async (
    entryId: string,
    latitude: number | null,
    longitude: number | null,
    notes?: string
  ) => {
    try {
      setError(null);

      const updateData: any = {
        clock_out_time: new Date().toISOString(),
        clock_out_latitude: latitude,
        clock_out_longitude: longitude
      };

      if (notes) {
        updateData.notes = notes;
      }

      const { error: updateError } = await withRetry(async () => {
        return await supabase
          .from('time_entries')
          .update(updateData)
          .eq('id', entryId);
      });

      if (updateError) throw updateError;
      await fetchTimeEntries();
    } catch (err) {
      console.error('Error clocking out:', err);
      throw err;
    }
  };

  const approveTimeEntry = async (entryId: string, approvedById: string) => {
    try {
      setError(null);

      const { error: updateError } = await withRetry(async () => {
        return await supabase
          .from('time_entries')
          .update({
            status: 'approved',
            approved_by: approvedById,
            approved_at: new Date().toISOString()
          })
          .eq('id', entryId);
      });

      if (updateError) throw updateError;
      await fetchTimeEntries();
    } catch (err) {
      console.error('Error approving time entry:', err);
      throw err;
    }
  };

  const rejectTimeEntry = async (entryId: string, approvedById: string, notes?: string) => {
    try {
      setError(null);

      const updateData: any = {
        status: 'rejected',
        approved_by: approvedById,
        approved_at: new Date().toISOString()
      };

      if (notes) {
        updateData.notes = notes;
      }

      const { error: updateError } = await withRetry(async () => {
        return await supabase
          .from('time_entries')
          .update(updateData)
          .eq('id', entryId);
      });

      if (updateError) throw updateError;
      await fetchTimeEntries();
    } catch (err) {
      console.error('Error rejecting time entry:', err);
      throw err;
    }
  };

  const getActiveEntry = useCallback((employeeId: string): TimeEntry | null => {
    return timeEntries.find(
      entry => entry.employeeId === employeeId && entry.clockOutTime === null
    ) || null;
  }, [timeEntries]);

  return {
    timeEntries,
    loading,
    error,
    clockIn,
    clockOut,
    approveTimeEntry,
    rejectTimeEntry,
    getActiveEntry,
    refetch: fetchTimeEntries
  };
}
